import { useCallback, useEffect, useMemo, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../../utils';
import { handleUpdateRowFields } from '../../../utils/calc-util';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { FormSchema, initialValues } from '../../../schema';
import type { PhieuChiInput } from '@/types/schemas';
import { useFormFieldState } from '../../../hooks';
import { useAuth } from '@/contexts/auth-context';
import type { FormDialogProps } from '../types';
import { useDetailRows, useTaxRows } from '.';

export const useFormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const { entityUnit } = useAuth();
  const formState = useFormFieldState(initialData);

  const detail = useDetailRows(initialData?.chi_tiet || [], undefined, handleUpdateRowFields);
  const tax = useTaxRows(initialData?.thue || [], undefined, handleUpdateRowFields);

  const repeatDetailRows = useRepeatRowsHandler({
    setRows: detail.setRows
  });
  const repeatTaxRows = useRepeatRowsHandler({
    setRows: tax.setRows,
    markDirty: () => setIsFormDirty(true)
  });

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
      setActiveTab('info');
    }
  }, [open]);

  const totals = useMemo(() => calculateTotals(detail.rows, tax.rows), [detail.rows, tax.rows]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const submit = useCallback(
    async (data: PhieuChiInput) => {
      const formData = transformFormData(
        data,
        formState.state,
        formMode,
        totals.tongTien,
        totals.tongThue,
        totals.tongThanhToan,
        detail.rows,
        tax.rows
      );

      await onSubmit?.({ ...formData, unit_id: entityUnit?.uuid });
      setIsFormDirty(false);
    },
    [
      detail.rows,
      entityUnit?.uuid,
      formMode,
      formState.state,
      onSubmit,
      tax.rows,
      totals.tongThanhToan,
      totals.tongThue,
      totals.tongTien
    ]
  );

  return {
    formMode,
    initialData,
    initialValues: initialData || initialValues,
    activeTab,
    tabChange: setActiveTab,
    title: getFormTitle(formMode, activeTab),
    actionButtons: getFormActionButtons(formMode, activeTab, {
      onAdd,
      onEdit,
      onDelete,
      onCopy,
      handleClose
    }),
    schema: FormSchema,
    formState,
    onClose: handleClose,
    submit,
    totals,
    isFormDirty,
    markDirty: () => setIsFormDirty(true),
    detail: {
      ...detail,
      repeatRows: repeatDetailRows
    },
    tax: {
      ...tax,
      repeatRows: repeatTaxRows
    }
  };
};

export type FormDialogModel = ReturnType<typeof useFormDialog>;
